import { useEffect, useRef } from 'react'
import { compact } from 'lodash'

import { UrlSearchParam } from 'consts/url'
import { useSearchParams } from 'router/useSearchParams'

import { UrlStateStorageKeys } from './consts'
import { decodeState, encodeState } from './utils/encodeState'

type LegacyFilterState = {
  departments: string[]
  specialisations: string[]
  queues: string[]
  agents: string[]
  from: number | null
  to: number | null
  start?: string
  end?: string
}

const LEGACY_PARAM_TO_STATE_KEY: Record<string, keyof LegacyFilterState> = {
  [UrlSearchParam.Departments]: 'departments',
  [UrlSearchParam.Specialisation]: 'specialisations',
  [UrlSearchParam.Queue]: 'queues',
  [UrlSearchParam.Agent]: 'agents',
  [UrlSearchParam.From]: 'from',
  [UrlSearchParam.To]: 'to',
  [UrlSearchParam.Start]: 'start',
  [UrlSearchParam.End]: 'end',
}

const LEGACY_PARAM_KEYS = Object.keys(LEGACY_PARAM_TO_STATE_KEY)

const parseLegacyIds = (value: string): string[] =>
  compact(value.split(',').map(id => id.trim()))

const parseLegacyTimestamp = (value: string): number | null => {
  const parsed = Number(value)

  return Number.isFinite(parsed) ? parsed : null
}

// this hook is used to migrate the legacy filters to the new hashed format
// will be removed after the migration is complete
export const useLegacyFiltersMigration = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const hasMigrated = useRef(false)

  useEffect(
    function migrateLegacyFiltersToHashedFormat() {
      if (hasMigrated.current) {
        return
      }

      const legacyParamsKeys = Array.from(searchParams.keys())
      const legacyParams = LEGACY_PARAM_KEYS.filter(key => legacyParamsKeys.includes(key))

      if (legacyParams.length === 0) {
        hasMigrated.current = true

        return
      }

      hasMigrated.current = true

      const existingState =
        decodeState<LegacyFilterState>(
          searchParams.get(UrlStateStorageKeys.GlobalFilters),
        ) || {}
      const legacyState: Partial<LegacyFilterState> = {}

      legacyParams.forEach(param => {
        const value = searchParams.get(param)

        if (!value) {
          return
        }

        const stateKey = LEGACY_PARAM_TO_STATE_KEY[param]

        if (stateKey === 'from' || stateKey === 'to') {
          legacyState[stateKey] = parseLegacyTimestamp(value)
        } else if (stateKey === 'start' || stateKey === 'end') {
          legacyState[stateKey] = value
        } else {
          legacyState[stateKey] = parseLegacyIds(value)
        }
      })

      const mergedState = { ...existingState, ...legacyState }
      const encodedState = encodeState(mergedState)

      const nextParams = new URLSearchParams(searchParams)

      legacyParams.forEach(param => nextParams.delete(param))

      if (encodedState) {
        nextParams.set(UrlStateStorageKeys.GlobalFilters, encodedState)
      }

      setSearchParams(nextParams, { replace: true })
    },
    [searchParams, setSearchParams],
  )
}
